import type { FC } from 'react';
import { Flex, useBreakpointValue } from '@chakra-ui/react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { SuggestedPrice } from '../../../../components/suggested-price';
import { DEFAULT_CURRENCY } from '../../../../constants';
import { Header, Text } from '../../../../ui-components';
import { SavingsAmountBadge } from '../../../../ui-components/savings-badge/SavingsAmountBadge';
import { isGiftCard, isShippingProtection } from '../../../../utils/product-utils';
import { isClient } from '../../../../utils/utils';
import type { ProductItemEditor } from '../../../../bff-api/baskets/model';

interface CartItemPriceProps {
    productItem?: ProductItemEditor;
}

export const CartItemPrice: FC<CartItemPriceProps> = ({ productItem }) => {
    const alignment: 'left' | 'right' | undefined = useBreakpointValue({
        base: 'left',
        'lg-sm': 'right',
    });

    if (!productItem) {
        return null;
    }

    const {
        basePrice = 0,
        price,
        priceAfterItemDiscount,
        c_prices = {},
        productId,
        quantity,
        c_savings,
    } = productItem;
    const { regularPrice, salePrice, suggestedPrice } = c_prices;
    const subTotal = isGiftCard(productItem)
        ? basePrice * quantity
        : (priceAfterItemDiscount ?? price ?? 0);

    const isSavingsEnabled = isClient && window.savingsFeatureMode?.includes('CART_LINE_ITEM');

    return (
        <Flex
            data-testid={`cart-item-product-price-${productId}`}
            direction="column"
            gap={1}
            alignItems={{ base: 'start', 'lg-sm': 'flex-end' }}
            width="max-content"
        >
            <Flex
                alignItems="center"
                wrap="wrap"
                columnGap={1}
                justifyContent={alignment === 'right' ? 'flex-end' : undefined}
            >
                <Header
                    id={`product${salePrice ? '-sale' : ''}-sub-total-${productId}`}
                    as="h4"
                    style="bold"
                    color={salePrice ? 'slt-red-error' : 'slt-pepper.1000'}
                    variant="header4"
                    align={alignment}
                >
                    <FormattedNumber
                        style="currency"
                        currency={DEFAULT_CURRENCY}
                        value={subTotal}
                    />
                </Header>

                {isSavingsEnabled && c_savings?.amount && (
                    <SavingsAmountBadge value={c_savings.amount} />
                )}
            </Flex>

            {!isGiftCard(productItem) && (
                <Flex
                    gap={1}
                    justifyContent={alignment === 'right' ? 'flex-end' : undefined}
                    wrap="wrap"
                >
                    {!isShippingProtection(productItem) && (
                        <Flex gap={1}>
                            <Text
                                id={`product-price-each-${productId}`}
                                style="regular"
                                size="small"
                                type="body"
                            >
                                <FormattedMessage id="price.each" defaultMessage="Each" />
                            </Text>

                            <Text
                                id={`product${
                                    salePrice ? '-sale' : ''
                                }unit-price-value-${productId}`}
                                style="regular"
                                size="small"
                                color={salePrice ? 'slt-red-error' : 'slt-pepper.1000'}
                            >
                                <FormattedNumber
                                    style="currency"
                                    currency={DEFAULT_CURRENCY}
                                    value={basePrice}
                                />
                            </Text>
                        </Flex>
                    )}

                    {suggestedPrice && (
                        <SuggestedPrice
                            id={productId}
                            price={suggestedPrice}
                            color="slt-pepper.600"
                            size="small"
                        />
                    )}

                    {!suggestedPrice && regularPrice ? (
                        <Text
                            id={`product-suggested-price-value-${productId}`}
                            style="regular"
                            size="small"
                            color="slt-pepper.600"
                            type="cross"
                        >
                            <FormattedNumber
                                style="currency"
                                currency={DEFAULT_CURRENCY}
                                value={regularPrice ?? 0}
                            />
                        </Text>
                    ) : null}
                </Flex>
            )}
        </Flex>
    );
};

CartItemPrice.defaultProps = {
    productItem: undefined,
};
